import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminBrokerAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Server, Users, TrendingUp,TrendingDown, Lock, Unlock, X, DollarSign, BarChart3, Shield, Activity, Settings2, Search, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function AdminBrokerPage() {
  const [tab, setTab] = useState('report');
  const [report, setReport] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [exposure, setExposure] = useState(null);
  const [spreadConfig, setSpreadConfig] = useState(null);
  const [spreadFilter, setSpreadFilter] = useState('all');
  const [spreadSearch, setSpreadSearch] = useState('');
  const [editingSpread, setEditingSpread] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingSpread, setSavingSpread] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showBalance, setShowBalance] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [balanceForm, setBalanceForm] = useState({ amount: '', operation: 'deposit', comment: '' });

  const loadData = async () => {
    setLoading(true);
    try {
      const [rpt, accs, tradesopen, tradesclosed, exp, spreads] = await Promise.all([
        adminBrokerAPI.getReport(), adminBrokerAPI.getAccounts(),
        adminBrokerAPI.getOpenTrades(), adminBrokerAPI.getClosedTrades(),
        adminBrokerAPI.getExposure(), adminBrokerAPI.getSpreads()
      ]);
      setReport(rpt.data);
      setAccounts(accs.data);
      setOpenTrades(tradesopen.data);
      setClosedTrades(tradesclosed.data);
      setExposure(exp.data);
      setSpreadConfig(spreads.data);
    } catch { toast.error('Failed to load broker data'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleFreeze = async (accNum, freeze) => {
    try {
      await adminBrokerAPI.freezeAccount({ account_number: accNum, freeze });
      toast.success(`Account ${freeze ? 'frozen' : 'unfrozen'}`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleBalance = async () => {
    if (!balanceForm.amount || parseFloat(balanceForm.amount) <= 0) { toast.error('Enter valid amount'); return; }
    try {
      await adminBrokerAPI.adjustBalance({
        account_number: showBalance, amount: parseFloat(balanceForm.amount),
        operation: balanceForm.operation, comment: balanceForm.comment
      });
      toast.success(`${balanceForm.operation} applied`);
      setShowBalance(null);
      setBalanceForm({ amount: '', operation: 'deposit', comment: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCloseTrade = async (tradeId, price) => {
    try {
      await adminBrokerAPI.closeTrade({ trade_id: tradeId, close_price: price });
      toast.success('Trade closed');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSaveSpread = async (symbol) => {
    const val = parseFloat(editValue);
    if (isNaN(val) || val <= 0 || val >= 10) { toast.error('Spread must be between 0 and 10%'); return; }
    setSavingSpread(true);
    try {
      await adminBrokerAPI.updateSpread({ symbol, spread_pct: val / 100 });
      toast.success(`Spread for ${symbol.toUpperCase()} updated to ${val}%`);
      setEditingSpread(null);
      setEditValue('');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update spread'); }
    setSavingSpread(false);
  };

  const getSymbolCategory = (sym) => {
    const s = sym.toLowerCase();
    const crypto = ['btc','eth','bnb','sol','xrp','ada','doge','dot','avax','link','matic','ltc','uni','atom','shib'];
    const forex = ['eurusd','gbpusd','usdjpy','audusd','usdchf','nzdusd','usdcad','eurgbp'];
    const indices = ['spx500','nas100','dji','ftse100','dax40','nikkei'];
    if (crypto.includes(s)) return 'crypto';
    if (forex.includes(s)) return 'forex';
    if (indices.includes(s)) return 'indices';
    return 'commodity';
  };

  const categoryColors = {
    crypto: 'bg-amber-500/15 text-amber-400',
    forex: 'bg-blue-500/15 text-blue-400',
    indices: 'bg-purple-500/15 text-purple-400',
    commodity: 'bg-emerald-500/15 text-emerald-400',
  };

  const tabs = [
    { key: 'report', label: 'Broker Report', icon: BarChart3 },
    { key: 'accounts', label: 'Accounts', icon: Users },
    { key: 'tradesopen', label: 'Open Trades', icon: TrendingUp },
    { key: 'tradesclosed', label: 'Closed Trades', icon: TrendingDown },
    { key: 'exposure', label: 'Risk & Exposure', icon: Shield },
    { key: 'spreads', label: 'Spread Config', icon: Settings2 },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-broker-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center">
            <Server className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Broker Controls</h1>
            <p className="text-xs text-[var(--text-secondary)]">Manage trading accounts, orders, spreads, and broker risk</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="broker-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`broker-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${tab === t.key ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {loading ? <div className="p-8 text-center text-sm text-[var(--text-secondary)]">Loading...</div> : (
          <>
            {/* Report Tab */}
            {tab === 'report' && report && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="broker-report">
                {[
                  { label: 'Platform', value: report.platform, color: 'text-green-400' },
                  { label: 'Total Accounts', value: report.total_accounts, color: 'text-[var(--text-primary)]' },
                  { label: 'Active', value: report.active_accounts, color: 'text-emerald-400' },
                  { label: 'Frozen', value: report.frozen_accounts, color: 'text-red-400' },
                  { label: 'Open Trades', value: report.open_trades, color: 'text-green-400' },
                  { label: 'Pending Orders', value: report.pending_orders, color: 'text-amber-400' },
                  { label: 'Closed Today', value: report.closed_today, color: 'text-[var(--text-secondary)]' },
                  { label: 'Spread Revenue', value: formatCurrency(report.total_spread_revenue), color: 'text-green-400' },
                  { label: 'Total Balance', value: formatCurrency(report.total_balance), color: 'text-[var(--text-primary)]' },
                  { label: 'Total Invested', value: formatCurrency(report.total_invested), color: 'text-amber-400' },
                ].map((item) => (
                  <div key={item.label} className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
                    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{item.label}</p>
                    <p className={`text-lg font-bold font-mono mt-1 ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Accounts Tab */}
            {tab === 'accounts' && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
                <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)] whitespace-nowrap">Trading Accounts ({accounts.length})</h3>
                  <div className="relative max-w-xs w-full">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
                    <input
                      type="text" placeholder="Search by User ID or Account #..."
                      value={accountSearch} onChange={e => setAccountSearch(e.target.value)}
                      data-testid="broker-account-search"
                      className="w-full pl-9 pr-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-green-500/50"
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                      <th className="text-left px-4 py-3">User ID</th>
                      <th className="text-left px-3 py-3">Account #</th>
                      <th className="text-left px-3 py-3">Type</th>
                      <th className="text-right px-3 py-3">Balance</th>
                      <th className="text-left px-3 py-3">Status</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts
                      .filter(a => {
                        if (!accountSearch) return true;
                        const q = accountSearch.toLowerCase();
                        return (a.user_id || '').toLowerCase().includes(q) || (a.account_number || '').toLowerCase().includes(q);
                      })
                      .map((a) => (
                      <tr key={a.account_number} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]" data-testid={`broker-account-row-${a.account_number}`}>
                        <td className="px-4 py-3 font-mono text-xs text-green-400" data-testid={`broker-userid-${a.account_number}`}>#{a.user_id}</td>
                        <td className="px-3 py-3 font-mono text-[var(--text-primary)]">{a.account_number}</td>
                        <td className="px-3 py-3 text-[var(--text-secondary)] capitalize text-xs">{a.account_type}</td>
                        <td className="px-3 py-3 text-right font-mono text-[var(--text-primary)]">{formatCurrency(a.balance)}</td>
                        <td className="px-3 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${a.status === 'active' ? 'bg-emerald-500/15 text-emerald-400' : a.status === 'frozen' ? 'bg-red-500/15 text-red-400' : 'bg-zinc-500/15 text-zinc-400'}`}>{a.status}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => setShowBalance(a.account_number)} title="Adjust Balance" data-testid={`broker-balance-${a.account_number}`}
                              className="p-1.5 rounded hover:bg-green-500/10 text-[var(--text-secondary)] hover:text-green-400"><DollarSign className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleFreeze(a.account_number, a.status !== 'frozen')} data-testid={`broker-freeze-${a.account_number}`}
                              title={a.status === 'frozen' ? 'Unfreeze' : 'Freeze'}
                              className={`p-1.5 rounded hover:bg-red-500/10 ${a.status === 'frozen' ? 'text-red-400' : 'text-[var(--text-secondary)] hover:text-red-400'}`}>
                              {a.status === 'frozen' ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {accounts.filter(a => {
                      if (!accountSearch) return true;
                      const q = accountSearch.toLowerCase();
                      return (a.user_id || '').toLowerCase().includes(q) || (a.account_number || '').toLowerCase().includes(q);
                    }).length === 0 && (
                      <tr><td colSpan={6} className="text-center py-8 text-xs text-[var(--text-tertiary)]">No accounts found</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {/* Open Trades Tab */}
            {tab === 'tradesopen' && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
                {openTrades.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No open trades</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                        <th className="text-left px-4 py-3">Ticket</th>
                        <th className="text-left px-3 py-3">Account</th>
                        <th className="text-left px-3 py-3">Symbol</th>
                        <th className="text-left px-3 py-3">Type</th>
                        <th className="text-right px-3 py-3">Volume</th>
                        <th className="text-right px-3 py-3">Amount</th>
                        <th className="text-right px-3 py-3">Entry</th>
                        <th className="text-right px-3 py-3">Current</th>
                        <th className="text-right px-3 py-3">P/L</th>
                        <th className="text-right px-3 py-3">Spread</th>
                        <th className="text-right px-3 py-3">Opened At</th>
                        <th className="text-right px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openTrades.map((t) => (
                        <tr key={t.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                          <td className="px-4 py-3 font-mono text-xs text-[var(--text-primary)]">#{t.ticket}</td>
                          <td className="px-3 py-3 font-mono text-xs text-[var(--text-secondary)]">{t.account_number}</td>
                          <td className="px-3 py-3 font-mono text-[var(--text-primary)]">{t.symbol?.toUpperCase()}</td>
                          <td className="px-3 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs ${t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{t.type?.toUpperCase()}</span>
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{t.volume}</td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.trade_amount)}</td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.entry_price)}</td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.current_price)}</td>
                          <td className={`px-3 py-3 text-right font-mono ${(t.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(t.profit_loss)}</td>
                          <td className="px-3 py-3 text-right font-mono text-xs text-amber-400">{t.spread_amount ? formatCurrency(t.spread_amount) : '-'}</td>
                          <td className="px-3 py-3 text-right text-[10px] text-[var(--text-secondary)]">{t.opened_at ? new Date(t.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => handleCloseTrade(t.id, t.current_price)} data-testid={`broker-close-${t.id}`}
                              className="px-3 py-1 rounded text-xs bg-red-500/15 text-red-400 hover:bg-red-500/25">Close</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {/* Closed Trades Tab */}
            {tab === 'tradesclosed' && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
                {closedTrades.length === 0 ? (
                  <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No closed trades</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                        <th className="text-left px-4 py-3">Ticket</th>
                        <th className="text-left px-3 py-3">Account</th>
                        <th className="text-left px-3 py-3">Symbol</th>
                        <th className="text-left px-3 py-3">Type</th>
                        <th className="text-right px-3 py-3">Volume</th>
                        <th className="text-right px-3 py-3">Amount</th>
                        <th className="text-right px-3 py-3">Entry</th>
                        <th className="text-right px-3 py-3">Current</th>
                        <th className="text-right px-3 py-3">P/L</th>
                          <th className="text-right px-3 py-3">Spread Rev</th>
                          <th className="text-right px-5 py-3">Opened At</th>
                        <th className="text-right px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closedTrades.map((t) => (
                        <tr key={t.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                          <td className="px-4 py-3 font-mono text-xs text-[var(--text-primary)]">#{t.ticket}</td>
                          <td className="px-3 py-3 font-mono text-xs text-[var(--text-secondary)]">{t.account_number}</td>
                          <td className="px-3 py-3 font-mono text-[var(--text-primary)]">{t.symbol?.toUpperCase()}</td>
                          <td className="px-3 py-3">
                            <span className={`px-2 py-0.5 rounded text-xs ${t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{t.type?.toUpperCase()}</span>
                          </td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{t.volume}</td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.trade_amount)}</td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.entry_price)}</td>
                          <td className="px-3 py-3 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.current_price)}</td>
                          <td className={`px-3 py-3 text-right font-mono ${(t.profit_loss || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(t.profit_loss)}</td>
                          <td className="px-3 py-3 text-right font-mono text-xs text-green-400">{formatCurrency(t.spread_revenue)}</td>
                           <td className="px-5 py-3 text-right text-[10px] text-[var(--text-secondary)]">{t.opened_at ? new Date(t.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td className="px-4 py-3 text-right">
                            <button disabled className="px-3 py-1 rounded text-xs bg-zinc-500/15 text-zinc-400 cursor-not-allowed">Closed</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Exposure Tab */}
            {tab === 'exposure' && exposure && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
                    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Total Buy Exposure</p>
                    <p className="text-lg font-bold font-mono text-emerald-400 mt-1">{formatCurrency(exposure.total_buy_amount)}</p>
                  </div>
                  <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
                    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Total Sell Exposure</p>
                    <p className="text-lg font-bold font-mono text-red-400 mt-1">{formatCurrency(exposure.total_sell_amount)}</p>
                  </div>
                  <div className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded">
                    <p className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">Net Exposure</p>
                    <p className={`text-lg font-bold font-mono mt-1 ${exposure.net_exposure >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(exposure.net_exposure)}</p>
                  </div>
                </div>

                {/* Per-Symbol Exposure */}
                {Object.keys(exposure.symbols || {}).length > 0 && (
                  <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
                    <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                      <h3 className="text-sm font-semibold text-[var(--text-primary)]">Exposure per Symbol</h3>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                          <th className="text-left px-4 py-2">Symbol</th>
                          <th className="text-right px-3 py-2">Buy Volume</th>
                          <th className="text-right px-3 py-2">Sell Volume</th>
                          <th className="text-right px-4 py-2">Net Exposure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(exposure.symbols).map(([sym, data]) => (
                          <tr key={sym} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                            <td className="px-4 py-2 font-mono text-[var(--text-primary)]">{sym}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-400">{data.buy_volume}</td>
                            <td className="px-3 py-2 text-right font-mono text-red-400">{data.sell_volume}</td>
                            <td className={`px-4 py-2 text-right font-mono ${data.net_exposure >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(data.net_exposure)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Largest Trades */}
                {(exposure.largest_trades || []).length > 0 && (
                  <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
                    <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                      <h3 className="text-sm font-semibold text-[var(--text-primary)]">Largest Open Trades</h3>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                          <th className="text-left px-4 py-2">Ticket</th>
                          <th className="text-left px-3 py-2">Symbol</th>
                          <th className="text-left px-3 py-2">Type</th>
                          <th className="text-right px-3 py-2">Amount</th>
                          <th className="text-right px-3 py-2">Spread</th>
                          <th className="text-right px-3 py-2">P/L</th>
                          <th className="text-right px-4 py-2">Opened At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exposure.largest_trades.map((t) => (
                          <tr key={t.ticket} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                            <td className="px-4 py-2 font-mono text-xs text-[var(--text-primary)]">#{t.ticket}</td>
                            <td className="px-3 py-2 font-mono text-[var(--text-primary)]">{t.symbol?.toUpperCase()}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${t.type === 'buy' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>{t.type?.toUpperCase()}</span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-[var(--text-secondary)]">{formatCurrency(t.trade_amount)}</td>
                            <td className="px-3 py-2 text-right font-mono text-amber-400 text-xs">{t.spread_amount ? formatCurrency(t.spread_amount) : '-'}</td>
                            <td className={`px-3 py-2 text-right font-mono ${(t.pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatCurrency(t.pnl)}</td>
                            <td className="px-4 py-2 text-right text-[10px] text-[var(--text-secondary)]">{t.opened_at ? new Date(t.opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Spreads Tab — redirects to new Spread Management page */}
            {tab === 'spreads' && (
              <div className="space-y-4" data-testid="spread-config-tab">
                <div className="p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg text-center space-y-3">
                  <DollarSign className="w-8 h-8 text-amber-400 mx-auto" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">Per-Trade Spread System</h3>
                  <p className="text-xs text-[var(--text-secondary)] max-w-md mx-auto">
                    Spread is now configured per-user and tracked per-trade. Set each user's default spread % from the Users page. View all spread totals and mark payments from the Spread Management page.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <a href="/admin/users" className="px-4 py-2 bg-green-500/15 text-green-400 rounded text-xs font-medium hover:bg-green-500/25 border border-green-500/20">
                      Set User Spread %
                    </a>
                    <a href="/admin/spread" className="px-4 py-2 bg-amber-500/15 text-amber-400 rounded text-xs font-medium hover:bg-amber-500/25 border border-amber-500/20">
                      Spread Management
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Balance Modal */}
        {showBalance && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="balance-modal">
            <div className="w-full max-w-md bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-[var(--text-primary)]">Adjust Balance — {showBalance}</h2>
                <button onClick={() => setShowBalance(null)}><X className="w-5 h-5 text-[var(--text-secondary)]" /></button>
              </div>
              <div className="flex gap-2">
                {['deposit', 'withdraw'].map((op) => (
                  <button key={op} onClick={() => setBalanceForm({ ...balanceForm, operation: op })}
                    className={`flex-1 py-2 rounded text-sm font-medium ${balanceForm.operation === op ? (op === 'deposit' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white') : 'bg-[var(--bg-primary)] text-[var(--text-secondary)] border border-[var(--border-subtle)]'}`}>
                    {op.charAt(0).toUpperCase() + op.slice(1)}
                  </button>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Amount (USD)</Label>
                <Input type="number" value={balanceForm.amount} onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                  placeholder="0.00" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Comment</Label>
                <Input value={balanceForm.comment} onChange={(e) => setBalanceForm({ ...balanceForm, comment: e.target.value })}
                  placeholder="Optional note" className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] h-10" />
              </div>
              <button onClick={handleBalance} className="w-full py-2.5 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium">Apply</button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
